import React, { lazy, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { AzureAD, IAzureADFunctionProps } from "react-aad-msal";
import { Typography } from "@mui/material";

import { AppRoutings } from "utility/enums/app-routings";
import Layout from "components/Layout";
import { azureAuthProvider } from "utility/authProvider";
import { Shared } from "utility/shared";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "redux/store";
import {
	fetchUserMenuAccessRights,
	setLoggedInUserDetail,
	setOrganizationDetail,
} from "redux/authStateSlice";
import { UserType } from "utility/enums/user-type";
import useAccessRight from "hooks/useAccessRight";
import { routes } from "./routes";
import environment from "environment";
import { vendorRoutes } from "./vendorRoutes";

const NotFoundComponent = lazy(() => import("components/NotFound"));
const SignInComponent = lazy(() => import("components/SignIn"));
const RedirectRouteComponent = lazy(() => import("./RedirectRoute"));
const CallbackComponent = lazy(() => import("./Callback"));
const SignUpComponent = lazy(() => import("components/SignUp"));
const AccessDeniedComponent = lazy(() => import("components/AccessDenied"));
const RequisitionConfirmationComponent = lazy(
	() => import("components/Requisition/EmailRequisitionConfirmation")
);
const SchedulerEventConfirmationComponent = lazy(
	() => import("components/SystemScheduler/EmailSchedulerEventConfirmation")
);
const BudgetAllocationConfirmationComponent = lazy(
	() => import("components/BudgetManagement/EmailBudgetAllocationConfirmation")
);
const ExternalUserConfirmationComponent = lazy(
	() =>
		import(
			"components/UserManagement/ExternalUser/EmailExternalUserConfirmation"
		)
);
const ContractConfirmationComponent = lazy(
	() =>
		import(
			"components/ContractManagement/NewContractEntry/EmailContractConfirmation"
		)
);
const BidConfirmationComponent = lazy(
	() => import("components/Bid/BidCommittee/EmailBidConfirmation")
);
const BidCommitteeDetail = lazy(
	() => import("components/Bid/BidCommittee/BidCommitteeDetail")
);
const EmailContractTermination = lazy(
	() =>
		import(
			"components/Settings/SystemSetup/ModuleSettingsTabContent/ContractModuleSetting/ExpirySettings/EmailContractTermination"
		)
);
const EmailContractArchive = lazy(
	() =>
		import(
			"components/Settings/SystemSetup/ModuleSettingsTabContent/ContractModuleSetting/ExpirySettings/EmailContractArchive"
		)
);
const ItemEmailConfirmation = lazy(
	() => import("components/MasterRecords/Item/EmailItemConfirmation")
);
const BidEmailConfirmation = lazy(
	() =>
		import(
			"components/Requisition/RequisitionConversionToBid/EmailBidConfirmation"
		)
);
const BidQueryEmailConfirmation = lazy(
	() =>
		import(
			"components/SupplyChainManagementPortal/BidQuery/EmailBidQueryConfirmation"
		)
);
const ChangeOrderRequestConfirmationComponent = lazy(
	() =>
		import(
			"components/BudgetManagement/ChangeOrderRequest/ChangeOrderRequestEntry/EmailChangeOrderRequestConfirmation"
		)
);

const RouterConfig: React.FC = () => {
	const dispatch = useDispatch();
	// const userAccessRight = useAccessRight();
	const userAccessRight = useAccessRight();
	const loggedInUserDetail = useSelector(
		(state: RootState) =>
			state.authState.userDetails && state.authState.userDetails
	); // fetch logged in user details from redux
	const userMenuAccessRights = useSelector(
		(state: RootState) => state.authState.userMenuAccessRights
	); // fetch menu list from redux based on logged in user rights

	const adUserAccessibleRoutes: string[] = [
		AppRoutings.RequisitionConfirmation,
		AppRoutings.SchedulerEventConfirmation,
		AppRoutings.SchedulerEventModuleConfirmation,
		AppRoutings.BudgetAllocationConfirmation,
		AppRoutings.ContractConfirmation,
		AppRoutings.ContractPOConfirmation,
		AppRoutings.ContractVariationConfirmation,
		AppRoutings.ChangeOrderRequestEntryConfirmation,
	];

	useEffect(() => {
		const userDetails = localStorage.getItem(
			Shared.localStorageKeys.userDetails
		);
		if (userDetails) {
			dispatch(setLoggedInUserDetail(JSON.parse(userDetails)));
		}
		const organizationDetails = localStorage.getItem(
			Shared.localStorageKeys.organizationDetails
		);
		if (organizationDetails) {
			dispatch(setOrganizationDetail(JSON.parse(organizationDetails)));
		}
	}, [dispatch]);

	useEffect(() => {
		// fetch user menu access rights every time the page is loaded
		if (
			loggedInUserDetail &&
			loggedInUserDetail.userTypeId === UserType.Internal &&
			window.location.pathname !== AppRoutings.Root &&
			window.location.pathname !== AppRoutings.SignIn &&
			window.location.pathname !== AppRoutings.SignUp &&
			window.location.pathname !== AppRoutings.Callback
		) {
			dispatch(fetchUserMenuAccessRights(false));
		}
	}, [loggedInUserDetail, dispatch]);

	const getRouteComponent = (
		path: string,
		Component:
			| React.LazyExoticComponent<React.FC<{}>>
			| React.ComponentType<{}>,
		pageName = "",
		moduleName = ""
	) => {
		return (
			<AzureAD provider={azureAuthProvider}>
				{({ accountInfo }: IAzureADFunctionProps) => {
					const isUserLoggedIn =
						loggedInUserDetail &&
						loggedInUserDetail.userTypeId &&
						(loggedInUserDetail.userTypeId !== UserType.External
							? accountInfo &&
							  (adUserAccessibleRoutes.includes(path)
									? loggedInUserDetail.email
									: true)
							: true);
					const isInternalUser =
						isUserLoggedIn &&
						loggedInUserDetail.userTypeId === UserType.Internal;
					const isExternalUser =
						isUserLoggedIn &&
						loggedInUserDetail.userTypeId === UserType.External;
					const isAdUser =
						isUserLoggedIn && loggedInUserDetail.userTypeId === UserType.AdUser;
					const pageAccessResponse: boolean | string =
						isInternalUser || isExternalUser
							? userAccessRight.hasPageAccess(pageName, path, moduleName)
							: true; // if user has right to access the page, hasPageAccess function will return true else error message
					const hasInternalUserAccess: boolean =
						userMenuAccessRights.length > 0;
					const hasAdUserAccess = adUserAccessibleRoutes.includes(path);
					return (
						<>
							{isInternalUser &&
								hasInternalUserAccess &&
								(typeof pageAccessResponse === "boolean" &&
								pageAccessResponse ? (
									<Component />
								) : (
									<Typography variant="h3" mt={2} textAlign="center">
										{pageAccessResponse}
									</Typography>
								))}
							{isAdUser &&
								(hasAdUserAccess ? (
									<Component />
								) : (
									<RedirectRouteComponent
										pathName={AppRoutings.AccessDenied}
										isAccessDeniedPage
									/>
								))}
							{isExternalUser &&
								(typeof pageAccessResponse === "boolean" &&
								pageAccessResponse ? (
									<Component />
								) : (
									<RedirectRouteComponent
										pathName={AppRoutings.AccessDenied}
										isAccessDeniedPage
									/>
								))}
							{!isUserLoggedIn && (
								<RedirectRouteComponent
									pathName={window.location.pathname}
									isEmailConfirmationPage={
										path === AppRoutings.RequisitionConfirmation ||
										path === AppRoutings.SchedulerEventConfirmation ||
										path === AppRoutings.SchedulerEventModuleConfirmation ||
										path === AppRoutings.BudgetAllocationConfirmation ||
										path === AppRoutings.ContractConfirmation ||
										path === AppRoutings.ContractPOConfirmation ||
										path === AppRoutings.ContractVariationConfirmation ||
										path === AppRoutings.BidCommitteeConfirmation ||
										path === AppRoutings.ContractTermination ||
										path === AppRoutings.ContractArchived ||
										path === AppRoutings.ItemConfirmation ||
										path === AppRoutings.ChangeOrderRequestEntryConfirmation
									}
								/>
							)}
						</>
					);
				}}
			</AzureAD>
		);
	};
	const confirmationRoutes = [
		{
			path: AppRoutings.RequisitionConfirmation,
			component: RequisitionConfirmationComponent,
		},
		{
			path: AppRoutings.SchedulerEventConfirmation,
			component: SchedulerEventConfirmationComponent,
		},
		{
			path: AppRoutings.SchedulerEventModuleConfirmation,
			component: SchedulerEventConfirmationComponent,
		},
		{
			path: AppRoutings.BudgetAllocationConfirmation,
			component: BudgetAllocationConfirmationComponent,
		},
		{
			path: AppRoutings.ContractConfirmation,
			component: ContractConfirmationComponent,
		},
		{
			path: AppRoutings.ContractPOConfirmation,
			component: RequisitionConfirmationComponent,
		},
		{
			path: AppRoutings.ContractVariationConfirmation,
			component: RequisitionConfirmationComponent,
		},
		{
			path: AppRoutings.BidCommitteeConfirmation,
			component: BidConfirmationComponent,
		},
		{
			path: AppRoutings.BidCommitteeDeclineConfirmation,
			component: BidConfirmationComponent,
		},
		{ path: AppRoutings.BidCommitteeView, component: BidCommitteeDetail },
		{ path: AppRoutings.BidCommitteeActivate, component: BidCommitteeDetail },
		{
			path: AppRoutings.ContractTermination,
			component: EmailContractTermination,
		},
		{ path: AppRoutings.ContractArchived, component: EmailContractArchive },
		{ path: AppRoutings.ItemConfirmation, component: ItemEmailConfirmation },
		{ path: AppRoutings.BidConfirmation, component: BidEmailConfirmation },
		{
			path: AppRoutings.ChangeOrderRequestEntryConfirmation,
			component: ChangeOrderRequestConfirmationComponent,
		},
	];
	return (
		<Routes>
			<Route path={AppRoutings.Root} element={<SignInComponent />} />
			<Route path={AppRoutings.SignIn} element={<SignInComponent />} />
			<Route path={AppRoutings.SignUp} element={<SignUpComponent />} />

			<Route
				path={AppRoutings.ExternalUserConfirmation}
				element={getRouteComponent(
					AppRoutings.ExternalUserConfirmation,
					ExternalUserConfirmationComponent
				)}
			/>

			<Route
				path={AppRoutings.BidQueryConfirmation}
				element={getRouteComponent(
					AppRoutings.BidQueryConfirmation,
					BidQueryEmailConfirmation
				)}
			/>
			{environment.buildTarget !== "vendor" &&
				confirmationRoutes.map(({ path, component }) => (
					<Route
						key={path}
						path={path}
						element={getRouteComponent(path, component)}
					/>
				))}
			<Route element={<Layout />} path="/">
				{(environment.buildTarget === "vendor" ? vendorRoutes : routes).map(
					({ path, Component, pageName, moduleName }, key) => (
						<Route
							key={key}
							path={path}
							element={getRouteComponent(path, Component, pageName, moduleName)}
						/>
					)
				)}
			</Route>
			<Route
				path={AppRoutings.AccessDenied}
				element={<AccessDeniedComponent />}
			/>
			<Route path={AppRoutings.Callback} element={<CallbackComponent />} />
			<Route path={AppRoutings.NotFound} element={<NotFoundComponent />} />
		</Routes>
	);
};

export default RouterConfig;
